import './ResultChart.css';
import { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { WalletContext } from './WalletContext';

function ResultChart() {
  const navigate = useNavigate();
  const location = useLocation();
  const { gameId } = useParams();
  const gameName = location.state?.gameName || 'Game';
  const { walletBalance } = useContext(WalletContext);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (gameId) {
      fetchResultData();
    }
  }, [gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchResultData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching results for game ID:', gameId);
      
      const response = await fetch(`https://backend.mainbazar777.site/api/normal-results/${gameId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch result data');
      }
      
      const data = await response.json();
      // console.log('API Response:', data);
      
      if (data.success && data.normal_results && data.normal_results.length > 0) {
        const formattedData = data.normal_results.map(item => {
          const result = item.normal_result;
          
          // Handle null date
          const date = result.created_at ? new Date(result.created_at) : new Date();
          
          const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
          const dateStr = date.getDate().toString().padStart(2, '0');
          const monthStr = date.toLocaleDateString('en-US', { month: 'short' });
          const yearStr = date.getFullYear();
          
          // Handle null values for open_panna, close_panna, and jodi
          const openPanna = result.open_panna != null ? result.open_panna.toString().padStart(3, '0') : '000';
          const closePanna = result.close_panna != null ? result.close_panna.toString().padStart(3, '0') : '000';
          const jodi = result.jodi != null ? result.jodi.toString().padStart(2, '0') : '**';
          
          // Create 3x3 grid: open_panna digits in left column, jodi in middle, close_panna in right column
          const numbers = {
            openDigits: [
              openPanna !== '000' ? parseInt(openPanna[0]) : '*',
              openPanna !== '000' ? parseInt(openPanna[1]) : '*',
              openPanna !== '000' ? parseInt(openPanna[2]) : '*'
            ],
            jodi: jodi,
            closeDigits: [
              closePanna !== '000' ? parseInt(closePanna[0]) : '*',
              closePanna !== '000' ? parseInt(closePanna[1]) : '*',
              closePanna !== '000' ? parseInt(closePanna[2]) : '*'
            ]
          };
          
          return {
            day: dayName,
            date: dateStr,
            month: monthStr,
            year: yearStr,
            numbers: numbers,
            jodi: jodi,
            rawData: result
          };
        }).reverse(); // Reverse to show most recent first
        
        setChartData(formattedData);
      } else {
        // No results available
        setChartData([]);
        setError('No results available for this game yet.');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching result data:', error);
      setError(error.message || 'Failed to load results');
      setLoading(false);
    }
  };

  const renderResultCard = (item, index) => {
    return (
      <div key={index} className="result-card">
        <div className="result-header">
          <span className="result-day">{item.day}</span>
          <span className="result-date">{item.date} {item.month} {item.year}</span>
        </div>
        <div className="result-numbers">
          <div className="number-row">
            <span className="number">{item.numbers.openDigits[0]}</span>
            <span className="number"></span>
            <span className="number">{item.numbers.closeDigits[0]}</span>
          </div>
          <div className="number-row">
            <span className="number">{item.numbers.openDigits[1]}</span>
            <span className="number middle highlighted">
              {item.jodi}
            </span>
            <span className="number">{item.numbers.closeDigits[1]}</span>
          </div>
          <div className="number-row">
            <span className="number">{item.numbers.openDigits[2]}</span>
            <span className="number"></span>
            <span className="number">{item.numbers.closeDigits[2]}</span>
          </div>
        </div>
      </div>
    );
  };

  // Group results into rows of 4
  const groupedData = [];
  for (let i = 0; i < chartData.length; i += 4) {
    groupedData.push(chartData.slice(i, i + 4));
  }

  return (
    <div className="result-chart-container">
      <div className="result-chart-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1>Chart</h1>
        <button className="wallet-btn" onClick={() => navigate("/funds")}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="6" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
            <path d="M2 10H22" stroke="white" strokeWidth="2"/>
            <circle cx="18" cy="15" r="1.5" fill="white"/>
          </svg>
          <span className="wallet-balance">₹ {walletBalance}</span>
        </button>
      </div>

      <div className="result-chart-content">
        <h2 className="chart-title">{gameName} Result Chart</h2>
        
        {loading ? (
          <div className="loading-message">Loading chart data...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : chartData.length === 0 ? (
          <div className="no-data-message">No results available for this game yet.</div>
        ) : (
          <div className="results-grid">
            {groupedData.map((row, rowIndex) => (
              <div key={rowIndex} className="results-row">
                {row.map((item, itemIndex) => renderResultCard(item, rowIndex * 4 + itemIndex))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ResultChart;
